import React, { useState, useEffect, useRef } from 'react';
import { Box, SxProps } from '@material-ui/system';
import { IconSize, IconsFolderL1, IconsSubFolder } from '../../types';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const SVGInject = require('@iconfu/svg-inject');

interface Props {
  folder?: IconsFolderL1;
  subfolder?: IconsSubFolder;
  fullPath?: string;
  size: IconSize;
  fillColor?: string;
  dataTestId?: string;
  wrapperStyles?: SxProps;
}

/**
 * Renders a remote SVG, useful for Icons
 * @param folder - folder in https://github.com/askporter/media/tree/main/media/icons defaults to system
 * @param subfolder - sub-folder(s)
 * @param fullpath - the full icon path excluding(usually from API responses), if this is is truthy then it will take
 * precedence and both folder & subfolder will not be used
 * @param size - IconSize types, e.g. SM, MD or LG
 * @param fillColor - targets the svg > g > g element and fills it with the provided hex value
 * @param wrapperStyles - additional styles for the surrounding div element
 */
const RemoteSvg: React.FC<Props> = ({
  folder = 'system',
  subfolder,
  fullPath,
  size,
  fillColor,
  dataTestId,
  wrapperStyles,
}: Props) => {
  const fill: SxProps = fillColor ? { '& svg > g > g': { fill: fillColor } } : undefined;
  const [opacity, setOpacity] = useState(0);

  // this approach is used to ensure there are no memory leaks caused when we attempt to setOpacity(1)
  const isMounted = useRef(true);
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  return (
    <Box
      data-testid={dataTestId || `remote-svg-icon-${subfolder || fullPath}`}
      display="flex"
      justifyContent="center"
      alignItems="center"
      sx={{ ...fill, width: size, height: size, '& img': { opacity }, ...wrapperStyles }}
    >
      <img
        src={fullPath ? `${fullPath}${size}px.svg` : `/media/icons/${folder}/${subfolder}/${size}px.svg`}
        onLoad={(e) => {
          SVGInject(e.target, {
            onAllFinish: () => {
              if (isMounted.current) setOpacity(1);
            },
          });
        }}
        alt=""
      />
    </Box>
  );
};

export default RemoteSvg;
